/**
 * KRX 금현물 수신 데이터 조회 API.
 *
 * 수신 서버(goldnara-krx-server)가 코스콤에서 받아 적재한 것을 읽기만 한다.
 * 쓰기는 S3 보관 객체 삭제 하나뿐이다 - 관리서버 DB 계정에 krx 스키마는
 * SELECT 권한만 있어 구조적으로도 그렇다.
 *
 * 권한: HQ(본사) 관리자 전용
 */
import api from './index'

const BASE = '/api/v1/krx'

/* ================================================================
 * 화면 초기화
 * ================================================================ */

/**
 * 화면이 처음 열릴 때 필요한 것.
 *
 * @returns {Promise} data: { serverIds, defaultBizDate, archiveConfigured, archiveBucket }
 */
export function getKrxMeta() {
  return api.get(`${BASE}/meta`)
}

/* ================================================================
 * 수신 현황
 * ================================================================ */

/**
 * 영업일별 수신 요약.
 *
 * fileCount / parsedCount / uploadedCount 가 어긋나면 어디가 막혔는지 보인다.
 * 셋이 같아야 정상이다 - 받았고, 읽었고, 올렸다.
 */
export function getReceiveSummary(params) {
  return api.get(`${BASE}/receive/summary`, { params })
}

/** 분 단위 원장. 데이터가 없는 분에도 빈 파일이 만들어진다. */
export function getL0Files(params) {
  return api.get(`${BASE}/receive/files`, { params })
}

/**
 * 수신 공백 구간.
 *
 * 비어 있지 않으면 그 시간의 시세는 영구 손실이다. 코스콤은 재전송해 주지 않는다.
 */
export function getMissingMinutes(params) {
  return api.get(`${BASE}/receive/missing`, { params })
}

/**
 * 접속/거절/종료 이력.
 *
 * REJECTED 가 있으면 허용목록(allowed-peers)에 빠진 주소가 peer 에 찍혀 있다.
 * 검증 날 "왜 안 들어오지"의 1순위 확인처다.
 */
export function getConnEvents(params) {
  return api.get(`${BASE}/receive/conn-events`, { params })
}

/** 장 운영 시각. SCHEDULE=예정(M401G), ACTUAL=실제 전환(A701G). */
export function getMarketDays(params) {
  return api.get(`${BASE}/market-days`, { params })
}

/* ================================================================
 * 시세
 * ================================================================ */

/** 체결 목록. 영업일이 비면 서버가 마지막 체결일로 채운다. */
export function getKrxTrades(params) {
  return api.get(`${BASE}/trades`, { params })
}

/** 종목 정보. 하루 세 번 들어오고 회차마다 덮어쓴다. */
export function getKrxIssues(params) {
  return api.get(`${BASE}/issues`, { params })
}

/** 현재가. 앱이 읽는 표와 같은 것을 본다. */
export function getKrxLastPrices() {
  return api.get(`${BASE}/last-prices`)
}

/* ================================================================
 * 원천 전문 (krx.frame)
 * ================================================================ */

/**
 * 전문 종류별 집계.
 *
 * 검증 날 가장 먼저 보는 것. 코스콤이 실제로 무엇을 보내는지가 여기서 드러난다.
 * 전용 표가 없는 전문(우선호가·시세 스냅샷·통계 등)도 전부 잡힌다.
 *
 * @returns {Promise} data: [{ msgType, frameCount, isinCount, firstRecvAt, lastRecvAt, frameLen }]
 */
export function getKrxFrameTypes(params) {
  return api.get(`${BASE}/frames/types`, { params })
}

/** 전문 목록. 본문(payload)은 싣지 않는다 — 상세에서만 내려온다. */
export function getKrxFrames(params) {
  return api.get(`${BASE}/frames`, { params })
}

/**
 * 전문 한 건의 전체 항목.
 *
 * 정의서상 한글 항목명 그대로 내려온다. 전용 화면이 없는 전문도
 * 이것으로 값을 끝까지 확인할 수 있다.
 *
 * @param {number} id      krx.frame.id
 * @param {string} bizDate 파티션 키라 같이 보내야 한다
 */
export function getKrxFrame(id, bizDate) {
  return api.get(`${BASE}/frames/${id}`, { params: { bizDate } })
}

/* ================================================================
 * 원본 보관 (S3)
 * ================================================================ */

/** 보관 목록. */
export function getArchiveObjects(params) {
  return api.get(`${BASE}/archive`, { params })
}

/** 전문 구성 미리보기. 내려받지 않고 무엇이 몇 건 들어왔는지 본다. */
export function getArchivePreview(params) {
  return api.get(`${BASE}/archive/preview`, { params })
}

/** 원본 내려받기. 바이너리라 blob 으로 받는다. */
export function downloadArchiveObject(key) {
  return api.get(`${BASE}/archive/download`, {
    params: { key },
    responseType: 'blob',
  })
}

/**
 * 원본 삭제.
 *
 * 로컬 파일은 한 달 뒤 수신 서버가 자동으로 지운다. 그 뒤로는 S3 사본이
 * 마지막 한 부다. 그래서 파일명을 그대로 다시 입력받아 확인한다.
 *
 * @param {string} key     S3 객체 키
 * @param {string} confirm 파일명. key 의 마지막 조각과 같아야 한다.
 */
export function deleteArchiveObject(key, confirm) {
  return api.delete(`${BASE}/archive`, { params: { key, confirm } })
}
